import { computed, makeObservable, observable, runInAction } from 'mobx';

import { inject, injectable } from '@servicetitan/react-ioc';

import {
    IMobileMaintenanceServiceEntryGridVm,
    MobileMaintenanceServiceEntryApi,
} from '@fleet-pro/mobile-webview-api';

import { RequestManager } from '../../common/stores/request-manager';
import { isAxiosCancellationError } from '../../common/utils/is-cancellation-error';
import { RequestState } from '../../common/utils/request-states';

@injectable()
export class ServiceEntriesDataStore extends RequestManager {
    @observable serviceEntries: IMobileMaintenanceServiceEntryGridVm[] = [];

    readonly getServiceEntriesRequestKey = 'getServiceEntriesRequestKey';

    constructor(
        @inject(MobileMaintenanceServiceEntryApi)
        private readonly mobileMaintenanceServiceEntryApi: MobileMaintenanceServiceEntryApi
    ) {
        super();

        makeObservable(this);
    }

    @computed get fetchServiceEntriesRequestState() {
        return this.getRequestState(this.getServiceEntriesRequestKey);
    }

    cancelGetServiceEntries = () => {
        this.cancelRequest(this.getServiceEntriesRequestKey);
    };

    fetchServiceEntries = async () => {
        this.cancelGetServiceEntries();
        this.resetAbortController(this.getServiceEntriesRequestKey);

        try {
            this.setRequestState(this.getServiceEntriesRequestKey, RequestState.Loading);

            const response =
                await this.mobileMaintenanceServiceEntryApi.client.getMobileMaintenanceServiceEntries(
                    {
                        signal: this.getAbortController(this.getServiceEntriesRequestKey)?.signal,
                    }
                );

            runInAction(() => {
                this.serviceEntries = response.data?.rows ?? [];
            });

            this.setRequestState(this.getServiceEntriesRequestKey, RequestState.Success);
        } catch (error) {
            if (isAxiosCancellationError(error)) {
                this.setRequestState(this.getServiceEntriesRequestKey, RequestState.Idle);
            } else {
                this.setRequestState(this.getServiceEntriesRequestKey, RequestState.Error);
            }

            throw error;
        }
    };
}
